import { FC, ReactNode } from "react";

import { Message, MessageAppearance, Messages } from "../../../general";
import { Body, Semibold } from "../../../typography";

import { IconButton } from "./IconButton";

import { ChatbotClassMap } from "../types";

type NoticeAppearance = "warning" | "info";

const APPEARANCE_MESSAGE: Record<NoticeAppearance, MessageAppearance> = {
  warning: Messages.Warning,
  info: Messages.Informational,
};

/* `Message` derives its accessible name from the appearance and has no branch for Informational,
   so an info notice would otherwise announce itself as "Success message". */
const APPEARANCE_LABEL: Record<NoticeAppearance, string> = {
  warning: "Warning message",
  info: "Informational message",
};

interface Props {
  readonly styles: ChatbotClassMap;
  readonly content: ReactNode;
  readonly label?: ReactNode;
  readonly appearance?: NoticeAppearance;
  readonly dismissLabel?: string;
  readonly testId?: string;
  readonly onDismiss: () => void;
}

/* Dismissible advisory pinned above the transcript, built on the shared `Message`. The wrapper
   carries the test ID because `Message` forwards its own to the layout primitive underneath, which
   prefixes it with "shelf-" or "stack-" depending on the width it measures. */
export const DisclaimerNotice: FC<Props> = ({
  styles,
  content,
  label,
  appearance = "warning",
  dismissLabel = "Dismiss disclaimer",
  testId = "chatbot-disclaimer",
  onDismiss,
}) => (
  <div className={styles.DisclaimerNotice} data-testid={testId}>
    <Message
      visible
      appearance={APPEARANCE_MESSAGE[appearance]}
      aria-label={APPEARANCE_LABEL[appearance]}
      actionContent={(
        <IconButton
          icon="x-close"
          iconSize="small"
          label={dismissLabel}
          tooltip="Dismiss"
          onClick={onDismiss}
          testId={`${testId}-dismiss`}
        />
      )}
    >
      <Body>
        {!!label && (
          <>
            <Semibold>{label}</Semibold>
            {": "}
          </>
        )}
        {content}
      </Body>
    </Message>
  </div>
);
